import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,


  Eye,
  Edit,
  Trash2,
  MoreVertical,
  Camera,
} from 'lucide-react';
import { usePhotographerEvents } from '@context/PhotographerEventsContext';
import { usePhotographer } from '@context/PhotographerContext';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import LoadingSpinner from '@components/common/LoadingSpinner';
import Alert from '@components/common/Alert';
import Modal from '@components/common/Modal';
import type { PhotographerEvent } from '@/types/photographer';
import { buildEventGalleryUrl } from '@utils/galleryUrl';
import CreateEventModal from './CreateEventModal';

const EventsManagement: React.FC = () => {
  const { events, loading, error, filters, fetchEvents, deleteEvent, setFilters } =
    usePhotographerEvents();
  const { photographer } = usePhotographer();

  const [showCreate, setShowCreate] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<PhotographerEvent | null>(null);
  const [searchTerm, setSearchTerm] = useState(filters.search);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setFilters({ search: value });
  };

  const handleStatusFilter = (status: string | null) => {
    setFilters({ status });
  };

  const handleDelete = async () => {
    if (eventToDelete) {
      try {
        await deleteEvent(eventToDelete.id);
        setShowDeleteModal(false);
        setEventToDelete(null);
      } catch (error) {
        // Error is handled by context
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'archived':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading && events.length === 0) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Events</h1>
          <p className="text-gray-600">Manage your photo events and galleries</p>
        </div>
        <Button
          variant="primary"
          icon={<Plus className="h-4 w-4" />}
          onClick={() => setShowCreate(true)}
        >
          Create Event
        </Button>
      </div>

      {/* Filters and Search */}
      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search events..."
              value={searchTerm}
              onChange={e => handleSearch(e.target.value)}
              leftIcon={<Search className="h-4 w-4" />}
            />
          </div>

          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Status:</span>
            <select
              value={filters.status || ''}
              onChange={e => handleStatusFilter(e.target.value || null)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="published">Published</option>
              <option value="draft">Draft</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <Alert
          type="error"
          title="Error loading events"
          message={error}
          actions={[
            {
              label: 'Retry',
              onClick: () => fetchEvents(),
              variant: 'primary',
            },
          ]}
        />
      )}

      {/* Events Grid */}
      {events.length === 0 && !loading ? (
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No events yet</h3>
          <p className="text-gray-500 mb-6">
            Create your first event to start showcasing your photography
          </p>
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setShowCreate(true)}
          >
            Create Your First Event
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map(event => (
            (() => {
              const galleryUrl = buildEventGalleryUrl(photographer, event.id);

              return (
                <div key={event.id} className="bg-white shadow rounded-lg overflow-hidden">
                  {/* Event Cover Image */}
                  <div className="aspect-w-16 aspect-h-9 bg-gray-200">
                    {event.coverImage ? (
                      <img
                        src={event.coverImage}
                        alt={event.title}
                        className="w-full h-48 object-cover"
                      />
                    ) : (
                      <div className="w-full h-48 flex items-center justify-center bg-gray-100">
                        <Camera className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                  </div>

                  {/* Event Details */}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(event.status)}`}
                      >
                        {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                      </span>
                      <div className="relative">
                        <button
                          type="button"
                          className="p-1 rounded-full hover:bg-gray-100"
                          onClick={() => {
                            // TODO: Implement dropdown menu
                          }}
                        >
                          <MoreVertical className="h-4 w-4 text-gray-400" />
                        </button>
                      </div>
                    </div>

                    <h3 className="text-lg font-medium text-gray-900 mb-1 truncate">
                      {event.title}
                    </h3>

                    <p className="text-sm text-gray-500 mb-2">{formatDate(event.eventDate)}</p>

                    {event.location && (
                      <p className="text-sm text-gray-500 mb-2 truncate">📍 {event.location}</p>
                    )}

                    <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
                      <span>{event.totalPhotos} photos</span>
                      <span>{event.analytics.totalOrders} orders</span>
                      <span>${event.analytics.totalRevenue.toFixed(2)}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="small"
                        icon={<Eye className="h-3 w-3" />}
                        disabled={!galleryUrl}
                        onClick={() => {
                          if (galleryUrl) {
                            window.open(galleryUrl, '_blank', 'noopener,noreferrer');
                          }
                        }}
                      >
                        View
                      </Button>
                      <Button
                        variant="outline"
                        size="small"
                        icon={<Edit className="h-3 w-3" />}
                        as={Link}
                        to={`/dashboard/events/${event.id}`}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="small"
                        icon={<Trash2 className="h-3 w-3" />}
                        onClick={() => {
                          setEventToDelete(event);
                          setShowDeleteModal(true);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })()
          ))}
        </div>
      )}

      {/* Create Event Modal */}
      <CreateEventModal open={showCreate} onClose={() => setShowCreate(false)} />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Event"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete "{eventToDelete?.title}"? This action cannot be undone
            and will also delete all associated photos and orders.
          </p>

          <div className="flex items-center justify-end space-x-3">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={loading}>
              Delete Event
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default EventsManagement;
